import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Shield,
  Users,
  Package,
  Briefcase,
  Calendar,
  Building2,
  RefreshCw
} from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

const TabIcon = ({ tab }) => {
  const icons = {
    roles: Shield,
    team: Users,
    assets: Package,
    services: Briefcase,
    bookings: Calendar,
    organization: Building2
  };
  const Icon = icons[tab] || Shield;
  return <Icon className="w-4 h-4" />;
};

const TestResultItem = ({ test }) => (
  <div className={`flex items-center justify-between p-2 rounded ${
    test.status === 'PASS' ? 'bg-green-50' : 'bg-red-50'
  }`}>
    <span className="text-sm font-medium">{test.test}</span>
    <div className="flex items-center gap-2">
      {test.status === 'PASS' ? (
        <CheckCircle className="w-4 h-4 text-green-600" />
      ) : (
        <XCircle className="w-4 h-4 text-red-600" />
      )}
      <span className={`text-xs ${test.status === 'PASS' ? 'text-green-600' : 'text-red-600'}`}>
        {test.details || test.error || test.status}
      </span>
    </div>
  </div>
);

const TabTestCard = ({ tabName, results, isExpanded, onToggle }) => {
  const passed = results?.passed || 0;
  const failed = results?.failed || 0;
  const total = passed + failed;
  const allPassed = failed === 0 && passed > 0;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${
      allPassed ? 'border-green-200 bg-green-50/50' : failed > 0 ? 'border-red-200 bg-red-50/50' : 'border-gray-200'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TabIcon tab={tabName} />
          <span className="font-medium capitalize">{tabName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">
            <span className="text-green-600">{passed}</span>
            <span className="text-gray-400">/</span>
            <span className={failed > 0 ? 'text-red-600' : 'text-gray-500'}>{total}</span>
          </span>
          {allPassed ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : failed > 0 ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : (
            <div className="w-5 h-5 bg-gray-200 rounded-full" />
          )}
        </div>
      </button>
      
      {isExpanded && results?.results && (
        <div className="border-t p-3 space-y-2 bg-white">
          {results.results.map((test, idx) => (
            <TestResultItem key={idx} test={test} />
          ))}
        </div>
      )}
    </div>
  );
};

const OperatorDashboardTestsSection = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedTabs, setExpandedTabs] = useState(new Set());
  const [runningTab, setRunningTab] = useState(null);

  const runAllTests = async () => {
    setLoading(true);
    setResults(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${BACKEND_URL}/api/health/test-suites/operator-dashboard/all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
        // Auto-expand failed tabs
        const failedTabs = new Set();
        Object.entries(data.tab_results || {}).forEach(([tab, res]) => {
          if (res.failed > 0) failedTabs.add(tab);
        });
        setExpandedTabs(failedTabs);
      } else {
        console.error('Test run failed:', response.status);
      }
    } catch (error) {
      console.error('Error running tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSingleTabTest = async (tabName) => {
    setRunningTab(tabName);
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${BACKEND_URL}/api/health/test-suites/operator-dashboard/${tabName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(prev => ({
          ...prev,
          tab_results: {
            ...(prev?.tab_results || {}),
            [tabName]: data
          },
          total_passed: (prev?.total_passed || 0) - (prev?.tab_results?.[tabName]?.passed || 0) + data.passed,
          total_failed: (prev?.total_failed || 0) - (prev?.tab_results?.[tabName]?.failed || 0) + data.failed
        }));
        setExpandedTabs(prev => new Set([...prev, tabName]));
      }
    } catch (error) {
      console.error(`Error running ${tabName} tests:`, error);
    } finally {
      setRunningTab(null);
    }
  };

  const toggleExpand = (tabName) => {
    setExpandedTabs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tabName)) {
        newSet.delete(tabName);
      } else {
        newSet.add(tabName);
      }
      return newSet;
    });
  };

  const tabs = ['roles', 'team', 'assets', 'services', 'bookings', 'organization'];

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Operator Dashboard v2 Tests
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              PostgreSQL endpoint tests for all dashboard tabs
            </p>
          </div>
          <Button 
            onClick={runAllTests} 
            disabled={loading}
            className="gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run All Tests
              </>
            )}
          </Button>
        </div>
        
        {results && (
          <div className="mt-4 flex items-center gap-4">
            <div className="text-2xl font-bold">
              {results.success_rate}
            </div>
            <div className="text-sm text-gray-500">
              <span className="text-green-600 font-medium">{results.total_passed} passed</span>
              {' / '}
              <span className={results.total_failed > 0 ? 'text-red-600 font-medium' : ''}>
                {results.total_failed} failed
              </span>
              {' / '}
              {results.total_tests} total
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4 space-y-3">
        {tabs.map(tab => (
          <div key={tab} className="flex items-center gap-2">
            <div className="flex-1">
              <TabTestCard
                tabName={tab}
                results={results?.tab_results?.[tab]}
                isExpanded={expandedTabs.has(tab)}
                onToggle={() => toggleExpand(tab)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => runSingleTabTest(tab)}
              disabled={loading || runningTab === tab}
              className="shrink-0"
            >
              {runningTab === tab ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        ))}
      </div>
      
      {results && (
        <div className="p-4 border-t bg-gray-50 text-xs text-gray-500">
          Last run: {new Date(results.timestamp).toLocaleString()}
          {' • '}
          Database: {results.database_backend}
        </div>
      )}
    </div>
  );
};

export default OperatorDashboardTestsSection;
